import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface PendingMessageAttachment {
  id: string;
  filename: string;
  url: string;
  isEncrypted: boolean;
}

export function hasComposerContent(
  promptText: string,
  options?: { pendingAttachments?: PendingMessageAttachment[] },
): boolean {
  const pending = options?.pendingAttachments?.length ?? 0;
  return promptText.trim().length > 0 || pending > 0;
}

export interface DocumentChatComposerContextValue {
  promptText: string;
  setPromptText: React.Dispatch<React.SetStateAction<string>>;
  clear: () => void;
  focusComposer: () => void;
  composerRootRef: React.RefObject<HTMLDivElement | null>;
  pendingAttachments: PendingMessageAttachment[];
  addPendingAttachment: (attachment: PendingMessageAttachment) => void;
  removePendingAttachment: (id: string) => void;
}

const DocumentChatComposerContext = createContext<
  DocumentChatComposerContextValue | undefined
>(undefined);

export function DocumentChatComposerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [promptText, setPromptText] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingMessageAttachment[]
  >([]);
  const composerRootRef = useRef<HTMLDivElement>(null);

  const addPendingAttachment = useCallback(
    (attachment: PendingMessageAttachment) => {
      setPendingAttachments((prev) => [...prev, attachment]);
    },
    [],
  );

  const removePendingAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clear = useCallback(() => {
    setPromptText('');
    setPendingAttachments([]);
  }, []);

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => {
      const root = composerRootRef.current;
      if (!root) return;

      const el = root.querySelector<HTMLElement>(
        '[data-chat-composer-text="true"]',
      );
      el?.focus();
    });
  }, []);

  const value = useMemo(
    () => ({
      promptText,
      setPromptText,
      clear,
      focusComposer,
      composerRootRef,
      pendingAttachments,
      addPendingAttachment,
      removePendingAttachment,
    }),
    [
      promptText,
      clear,
      focusComposer,
      pendingAttachments,
      addPendingAttachment,
      removePendingAttachment,
    ],
  );

  return (
    <DocumentChatComposerContext.Provider value={value}>
      {children}
    </DocumentChatComposerContext.Provider>
  );
}

export function useDocumentChatComposer() {
  const ctx = useContext(DocumentChatComposerContext);
  if (!ctx) {
    throw new Error(
      'useDocumentChatComposer must be used within DocumentChatComposerProvider',
    );
  }

  return ctx;
}
